'use client';

import type { ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  CheckmarkBadge01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUpRight01Icon,
  ComputerTerminal01Icon,
  PlayIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { LabelDefault, MetaDefault } from '../../../components/typography';
import { terminalCodeBlockStyles } from '../../chat/components/TerminalContent';
import {
  useOpenInTerminal,
  useSendToTerminal,
  type CliTerminalTarget,
} from '../../chat/cli-replay';
import { useChatExpansionState } from '../../chat/expansion/chat-expansion-state';
import { formatDurationBySeconds } from '../../chat/trace-view/utils';
import { CliClampedBlock } from './CliClampedBlock';
import { CliCopyButton } from './CliCopyButton';

const CLI_COMMAND_COLLAPSED_MAX_HEIGHT_PX = 160;
const CLI_OUTPUT_COLLAPSED_MAX_HEIGHT_PX = 240;

type CliResult = {
  stdout?: string;
  stderr?: string;
  exitCode?: number;
};
type CliEditorProps = {
  command: string;
  title?: string;
  result?: CliResult;
  duration?: number;
  isEvidence?: boolean;
  headerActions?: ReactNode;
  footer?: ReactNode;
  hideCommandToggle?: boolean;
  commandInitiallyOpen?: boolean;
  outputInitiallyOpen?: boolean;
  expansionKey?: string;
  terminalTarget?: CliTerminalTarget;
};

export function CliEditor({
  command,
  title,
  result,
  duration,
  isEvidence,
  headerActions,
  footer,
  hideCommandToggle,
  commandInitiallyOpen = true,
  outputInitiallyOpen = true,
  expansionKey,
  terminalTarget,
}: CliEditorProps) {
  const tChat = useTranslations('designSystem.chat');
  const tEvidence = useTranslations('designSystem.artifacts.evidenceBadge');
  const tArtifacts = useTranslations('designSystem.artifacts');
  const sendToTerminal = useSendToTerminal();
  const openInTerminal = useOpenInTerminal();
  const [isCommandExpanded, setIsCommandExpanded] = useChatExpansionState({
    expansionKey: expansionKey ? `${expansionKey}:command` : undefined,
    defaultExpanded: commandInitiallyOpen,
  });
  const [isOutputExpanded, setIsOutputExpanded] = useChatExpansionState({
    expansionKey: expansionKey ? `${expansionKey}:output` : undefined,
    defaultExpanded: outputInitiallyOpen,
  });
  const headerTitle = title ?? tChat('cliExecution');
  const exitCode = result?.exitCode;
  const outputText = [result?.stdout, result?.stderr]
    .filter(Boolean)
    .join('\n');
  const hasOutput = outputText.length > 0;

  const hasResultMetadata = exitCode !== undefined || duration !== undefined;
  const hasOutputSection = hasOutput || hasResultMetadata;
  const hasError = exitCode !== undefined && exitCode !== 0;
  const isCommandVisible = hideCommandToggle || isCommandExpanded;
  const commandBlock = (
    <CliClampedBlock
      collapsedMaxHeightPx={CLI_COMMAND_COLLAPSED_MAX_HEIGHT_PX}
      expansionKey={expansionKey ? `${expansionKey}:command-full` : undefined}
      className={terminalCodeBlockStyles}
      borderRadius="0"
      borderTopWidth={hideCommandToggle ? '0' : '1px'}
      borderTopColor="border.neutral.muted"
      w="full"
      bg={{ _light: 'neutral.light.2', _dark: 'neutral.dark.1' }}
      color={{ _light: 'neutral.light.12', _dark: 'neutral.dark.12' }}
      fontFamily="mono"
    >
      <Box as="span" fontFamily="mono">
        ${' '}
      </Box>
      {command}
    </CliClampedBlock>
  );

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        gap={2}
        px={2}
        h="28px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center" minW={0} flex={1}>
          <Box display="flex" alignItems="center" color="fg.muted">
            <ComputerTerminal01Icon size={12} />
          </Box>
          <Tooltip title={headerTitle} openDelay={500}>
            <LabelDefault as="span" color="fg.default" truncate minW={0}>
              {headerTitle}
            </LabelDefault>
          </Tooltip>
        </HStack>
        <HStack gap={1} alignItems="center" flexShrink={0}>
          {isEvidence && (
            <Tooltip title={tEvidence('markedAsEvidence')}>
              <Box color="green.9" display="flex" alignItems="center">
                <CheckmarkBadge01Icon size={12} />
              </Box>
            </Tooltip>
          )}
          {headerActions}
          <CliCopyButton text={command} />
          {openInTerminal && terminalTarget && (
            <Tooltip title={tChat('openInTerminal')} openDelay={300}>
              <IconButton
                variant="ghost"
                size="2xs"
                aria-label={tChat('openInTerminal')}
                color="fg.muted"
                _hover={{ color: 'fg.default' }}
                onClick={() => openInTerminal(terminalTarget)}
              >
                <ArrowUpRight01Icon size={12} />
              </IconButton>
            </Tooltip>
          )}
          {sendToTerminal && (
            <Tooltip title={tChat('replayInTerminal')} openDelay={300}>
              <IconButton
                variant="ghost"
                size="2xs"
                aria-label={tChat('replayInTerminal')}
                color="fg.muted"
                _hover={{ color: 'fg.default' }}
                onClick={() => sendToTerminal({ command })}
              >
                <PlayIcon size={12} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      <Box width="100%">
        {hideCommandToggle ? null : (
          <HStack
            px={2}
            h="28px"
            cursor="pointer"
            onClick={() => setIsCommandExpanded(!isCommandExpanded)}
            width="100%"
            gap={1.5}
            alignItems="center"
            bg="transparent"
          >
            <Box display="flex" alignItems="center" color="fg.muted">
              {isCommandExpanded ? (
                <ArrowDown01Icon size={10} />
              ) : (
                <ArrowRight01Icon size={10} />
              )}
            </Box>
            <LabelDefault as="span" color="fg.muted">
              {tChat('cliCommand')}
            </LabelDefault>
          </HStack>
        )}
        {isCommandVisible ? commandBlock : null}
      </Box>

      {hasOutputSection && (
        <Box
          borderTopWidth="1px"
          borderTopColor="border.neutral.muted"
          width="100%"
        >
          <HStack
            px={2}
            h="28px"
            cursor={hasOutput ? 'pointer' : 'default'}
            onClick={
              hasOutput
                ? () => setIsOutputExpanded(!isOutputExpanded)
                : undefined
            }
            width="100%"
            gap={1.5}
            alignItems="center"
            justify="space-between"
            bg="transparent"
          >
            <HStack gap={1.5} alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                color="fg.muted"
                opacity={hasOutput ? 1 : 0}
              >
                {isOutputExpanded ? (
                  <ArrowDown01Icon size={10} />
                ) : (
                  <ArrowRight01Icon size={10} />
                )}
              </Box>
              <LabelDefault as="span" color="fg.muted">
                {tChat('cliOutput')}
              </LabelDefault>
            </HStack>
            <HStack gap={1.5} alignItems="center">
              {hasOutput ? <CliCopyButton text={outputText} /> : null}
              {hasError && (
                <LabelDefault as="span" color="fg.error">
                  {tArtifacts('exitCode', { code: exitCode })}
                </LabelDefault>
              )}
              {exitCode === 0 && (
                <LabelDefault as="span" color="fg.success">
                  {tArtifacts('exitCode', { code: 0 })}
                </LabelDefault>
              )}
              {duration !== undefined && (
                <MetaDefault color="fg.muted">
                  {formatDurationBySeconds(duration)}
                </MetaDefault>
              )}
            </HStack>
          </HStack>

          {hasOutput && isOutputExpanded && (
            <CliClampedBlock
              collapsedMaxHeightPx={CLI_OUTPUT_COLLAPSED_MAX_HEIGHT_PX}
              expansionKey={
                expansionKey ? `${expansionKey}:output-full` : undefined
              }
              className={terminalCodeBlockStyles}
              borderRadius="0"
              borderTopWidth="1px"
              borderTopColor="border.neutral.muted"
              w="full"
              bg={{ _light: 'neutral.light.2', _dark: 'neutral.dark.1' }}
              color={{ _light: 'neutral.light.12', _dark: 'neutral.dark.12' }}
              fontFamily="mono"
            >
              {result?.stdout}
              {result?.stderr && (
                <Box
                  as="span"
                  color={{ _light: 'red.light.11', _dark: 'red.dark.11' }}
                >
                  {result.stdout ? '\n' : ''}
                  {result.stderr}
                </Box>
              )}
            </CliClampedBlock>
          )}
        </Box>
      )}
      {footer}
    </Box>
  );
}
